// SPDX-License-Identifier: MIT
// Local SAS decryption utilities (AES-256-GCM + PBKDF2)
// Security: Do NOT log secrets or plaintexts. Feature is gated elsewhere.

// Prefix constant for tokens
export const ENC_PREFIX = 'enc:v1:';

export function isEncryptedSasToken(input: string | undefined | null): boolean {
  if (!input) return false;
  return String(input).trim().startsWith(ENC_PREFIX);
}

// Base64url helpers
function toBase64Url(bytes: Uint8Array): string {
  let b64: string;
  if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
    b64 = Buffer.from(bytes).toString('base64');
  } else {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    b64 = btoa(binary);
  }
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 ? 4 - (b64.length % 4) : 0;
  const b64p = b64 + '='.repeat(pad);
  if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
    return new Uint8Array(Buffer.from(b64p, 'base64'));
  }
  const binary = atob(b64p);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return arr;
}

function utf8Encode(s: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s);
  // Fallback (Node < v11): use Buffer
  return new Uint8Array(Buffer.from(s, 'utf-8'));
}

function utf8Decode(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(view);
  return Buffer.from(view).toString('utf-8');
}

async function getSubtle(): Promise<SubtleCrypto> {
  const c: any = (globalThis as any).crypto;
  if (c?.subtle) return c.subtle as SubtleCrypto;
  // Node: dynamically import webcrypto
  try {
    const nodeCrypto = await import('node:crypto');
    return nodeCrypto.webcrypto.subtle as SubtleCrypto;
  } catch {
    throw new Error('Web Crypto API (SubtleCrypto) not available');
  }
}

// Note: we pass Uint8Array views directly to SubtleCrypto to avoid cross-realm ArrayBuffer issues
// in mixed environments (Node + JSDOM).

async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
  iterations: number
): Promise<CryptoKey> {
  const subtle = await getSubtle();
  const passBytes = utf8Encode(passphrase);
  // Pass Uint8Array view directly (cast to BufferSource for TS)
  const baseKey = await subtle.importKey(
    'raw',
    passBytes as unknown as BufferSource,
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return await subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as unknown as BufferSource, iterations },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function getRandomBytes(len: number): Promise<Uint8Array> {
  const out = new Uint8Array(len);
  const c: any = (globalThis as any).crypto;
  if (c?.getRandomValues) {
    c.getRandomValues(out);
    return out;
  }
  // Node fallback
  const nodeCrypto = await import('node:crypto');
  return new Uint8Array(nodeCrypto.randomBytes(len));
}

export interface EncryptOptions {
  iterations?: number;
}

// Provided for tests and PowerShell interop validation; not used in production UI path.
export async function encryptSasToken(
  plaintextSas: string,
  passphrase: string,
  opts?: EncryptOptions
): Promise<string> {
  const iterations = opts?.iterations ?? getDefaultIterations();
  const salt = await getRandomBytes(16);
  const iv = await getRandomBytes(12);
  const key = await deriveKey(passphrase, salt, iterations);
  const subtle = await getSubtle();
  const ct = await subtle.encrypt(
    { name: 'AES-GCM', iv: iv as unknown as BufferSource },
    key,
    utf8Encode(plaintextSas) as unknown as BufferSource
  );
  const payload = new Uint8Array(salt.length + iv.length + (ct as ArrayBuffer).byteLength);
  payload.set(salt, 0);
  payload.set(iv, salt.length);
  payload.set(new Uint8Array(ct as ArrayBuffer), salt.length + iv.length);
  return ENC_PREFIX + toBase64Url(payload);
}

export interface DecryptOptions {
  iterations?: number;
}

export async function decryptSasToken(
  encToken: string,
  passphrase: string,
  opts?: DecryptOptions
): Promise<string> {
  if (!isEncryptedSasToken(encToken)) throw new Error('INVALID_PREFIX');
  const iterations = opts?.iterations ?? getDefaultIterations();
  const b64 = encToken.slice(ENC_PREFIX.length);
  const payload = fromBase64Url(b64);
  if (payload.length < 16 + 12 + 16) throw new Error('PAYLOAD_TOO_SHORT');
  const salt = payload.slice(0, 16);
  const iv = payload.slice(16, 28);
  const ct = payload.slice(28);
  const key = await deriveKey(passphrase, salt, iterations);
  const subtle = await getSubtle();
  try {
    const pt = await subtle.decrypt(
      { name: 'AES-GCM', iv: iv as unknown as BufferSource },
      key,
      ct as unknown as BufferSource
    );
    return utf8Decode(pt);
  } catch {
    // Authentication failure or wrong key/iterations
    throw new Error('DECRYPT_FAILED');
  }
}

export function getDefaultIterations(): number {
  const viteVal = (import.meta as any)?.env?.VITE_LOCAL_SAS_PBKDF2_ITERATIONS;
  const nodeVal =
    typeof process !== 'undefined'
      ? (process as any).env?.VITE_LOCAL_SAS_PBKDF2_ITERATIONS
      : undefined;
  const raw = viteVal || nodeVal;
  const n = raw ? Number(raw) : 100_000;
  return Number.isFinite(n) && n > 0 ? n : 100_000;
}
